import React, { useState } from 'react';
import { Button, Icon } from '@/components/atoms';
import { Input } from '@kisanlink/ui-commons';
import { useWarehousesList, useCreateInventoryBatchMutation } from '@/hooks/useErpApi';

interface BatchFormProps {
  variantId: string;
  variantName: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const BatchForm: React.FC<BatchFormProps> = ({
  variantId,
  variantName,
  onSuccess,
  onCancel,
}) => {
  const { data: warehouses, isLoading: warehousesLoading } = useWarehousesList();
  const { mutate: createBatch, isLoading } = useCreateInventoryBatchMutation();

  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    warehouse_id: '',
    quantity: 0,
    cost_price: 0,
    expiry_date: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    let processedValue: string | number = value;

    if (['quantity', 'cost_price'].includes(name)) {
      processedValue = value === '' ? 0 : parseFloat(value);
    }

    setFormData((prev) => ({ ...prev, [name]: processedValue }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.warehouse_id) {
      setError('Please select a warehouse');
      return;
    }

    if (formData.quantity <= 0) {
      setError('Quantity must be greater than 0');
      return;
    }

    if (formData.cost_price < 0) {
      setError('Cost price cannot be negative');
      return;
    }

    if (!formData.expiry_date) {
      setError('Expiry date is required');
      return;
    }

    try {
      await createBatch({
        variant_id: variantId,
        warehouse_id: formData.warehouse_id,
        quantity: formData.quantity,
        cost_price: formData.cost_price,
        expiry_date: formData.expiry_date,
      });

      onSuccess?.();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      setError(`Failed to create batch: ${errorMessage}`);
    }
  };

  const isFormLoading = isLoading || warehousesLoading;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-error-50 border border-error-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="TriangleAlert" size={20} className="text-error-600 flex-shrink-0 mt-0.5" />
            <p className="text-error-700 text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Variant Info */}
      <div className="bg-neutral-50 rounded-lg p-4">
        <p className="text-sm font-medium text-neutral-700">Adding batch for:</p>
        <p className="text-base font-semibold text-neutral-900 mt-1">{variantName}</p>
      </div>

      {/* Warehouse Selection */}
      <div>
        <label htmlFor="warehouse_id" className="block text-sm font-medium text-neutral-700 mb-2">
          Warehouse <span className="text-error-600">*</span>
        </label>
        <select
          id="warehouse_id"
          name="warehouse_id"
          value={formData.warehouse_id}
          onChange={handleChange}
          disabled={isFormLoading}
          className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-neutral-100 disabled:cursor-not-allowed"
        >
          <option value="">Select a warehouse...</option>
          {(warehouses || []).map((warehouse) => (
            <option key={warehouse.id} value={warehouse.id}>
              {warehouse.name}
              {warehouse.address
                ? ` - ${warehouse.address.district || ''}, ${warehouse.address.state || ''}`
                : ''}
            </option>
          ))}
        </select>
      </div>

      {/* Quantity and Cost Price Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="quantity" className="block text-sm font-medium text-neutral-700 mb-2">
            Quantity <span className="text-error-600">*</span>
          </label>
          <Input
            id="quantity"
            name="quantity"
            type="number"
            value={formData.quantity || ''}
            onChange={handleChange}
            placeholder="0"
            min={1}
            step={1}
            disabled={isFormLoading}
          />
          <p className="text-neutral-600 text-xs mt-1">Number of units in this batch</p>
        </div>

        <div>
          <label htmlFor="cost_price" className="block text-sm font-medium text-neutral-700 mb-2">
            Cost Price (₹) <span className="text-error-600">*</span>
          </label>
          <Input
            id="cost_price"
            name="cost_price"
            type="number"
            value={formData.cost_price || ''}
            onChange={handleChange}
            placeholder="0.00"
            min={0}
            step={0.01}
            disabled={isFormLoading}
          />
          <p className="text-neutral-600 text-xs mt-1">Cost per unit</p>
        </div>
      </div>

      {/* Expiry Date */}
      <div>
        <label htmlFor="expiry_date" className="block text-sm font-medium text-neutral-700 mb-2">
          Expiry Date <span className="text-error-600">*</span>
        </label>
        <Input
          id="expiry_date"
          name="expiry_date"
          type="date"
          value={formData.expiry_date}
          onChange={handleChange}
          disabled={isFormLoading}
        />
        <p className="text-neutral-600 text-xs mt-1">
          Date when this batch expires (FEFO inventory management)
        </p>
      </div>

      {/* Summary */}
      <div className="bg-info-50 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-info-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-sm text-info-800">
            <p className="font-medium">Batch Value</p>
            <p className="mt-1">
              {formData.quantity} units × ₹{formData.cost_price.toFixed(2)} ={' '}
              <span className="font-semibold">
                ₹{(formData.quantity * formData.cost_price).toFixed(2)}
              </span>
            </p>
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t border-neutral-200">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={isFormLoading}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={isFormLoading}>
          {isFormLoading ? (
            <>
              <Icon name="Loader" size={16} className="mr-2 animate-spin" />
              Creating...
            </>
          ) : (
            <>
              <Icon name="Plus" size={16} className="mr-2" />
              Create Batch
            </>
          )}
        </Button>
      </div>
    </form>
  );
};
